import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import toast from 'react-hot-toast';
import Cookies from 'js-cookie';
import { getAccessToken, clearAuthData, removeAccessToken, removeUserId } from 'utils/sessionStorage';
import { shouldSuppressCamSessionInterrupt } from 'utils/camAuth';
import { reportMissingData, reportUnexpected } from '../sentry';

const http = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',

  headers: {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Methods': 'GET,OPTIONS,PATCH,DELETE,POST,PUT',
    'Access-Control-Allow-Headers':
      'X-CSRF-Token, X-Requested-With, Accept, Accept-Version, Content-Length, Content-MD5, Content-Type, Date, X-Api-Version, Authorization',
  },
});

http.interceptors.request.use(
  function (config: AxiosRequestConfig) {
    const auToken = getAccessToken();
    if (!config) {
      config = {};
    }
    if (!config.headers) {
      config.headers = {};
    }
    config.headers.Authorization = 'Bearer ' + auToken;
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

const handleRevokedSession = () => {
  if (shouldSuppressCamSessionInterrupt()) {
    return;
  }
  clearAuthData();
  removeAccessToken();
  removeUserId();
  Cookies.remove('refresh_token');
  window.location.href = '/#/public/login';
};

const FRIENDLY_DEFAULT = "That didn't go through — please try again in a moment.";

function friendlyByStatus(status?: number): string {
  if (!status) {
    return "You appear to be offline. Your changes are safe — we'll finish up once you're reconnected.";
  }
  if (status >= 500) {
    return 'Something on our end hiccuped. Our team has been notified — please try again shortly.';
  }
  if (status === 429) {
    return "That's a lot at once — give it a second and try again.";
  }
  if (status === 413) {
    return 'That entry is a little too large to save.';
  }
  if (status === 404) {
    return "We couldn't find that — it may have been updated. Try refreshing the page.";
  }
  if (status === 403) {
    return "You don't have access to that right now.";
  }
  return FRIENDLY_DEFAULT;
}

function looksTechnical(text: string): boolean {
  if (!text) return true;
  if (text.length > 140) return true;
  return /invalid|error|exception|failed|null|undefined|constraint|ECONN|stack|\bDto\b|query/i.test(
    text
  );
}

function userFacingClientMessage(rawMessage: unknown, status?: number): string {
  const raw = Array.isArray(rawMessage)
    ? rawMessage.join(', ')
    : rawMessage
    ? String(rawMessage)
    : '';
  return looksTechnical(raw) ? friendlyByStatus(status) : raw;
}

function isGetRequest(error: any): boolean {
  return String(error?.config?.method || 'get').toLowerCase() === 'get';
}

http.interceptors.response.use(
  function (response: AxiosResponse) {
    let returnData: any = response.data;
    const statusCode = response.status;

    if (statusCode === 430 && returnData?.code === 'SESSION_EXPIRED') {
      clearAuthData();
      removeAccessToken();
      removeUserId();
      Cookies.remove('refresh_token');
      window.location.href = '/#/public/session-conflict';
      return Promise.reject(new Error('SESSION_EXPIRED'));
    }

    if (returnData?.errorCode === 430) {
      handleRevokedSession();
      return Promise.reject(new Error(returnData?.message || 'Session revoked'));
    }

    // Null/empty GET bodies (e.g. college-university with no row) must not throw.
    if (
      returnData &&
      typeof returnData === 'object' &&
      typeof returnData.status === 'number' &&
      returnData.status >= 400
    ) {
      toast(userFacingClientMessage(returnData?.message, returnData.status));
      return Promise.reject();
    }

    return response.data;
  },
  (error) => {
    try {
      if (error.response) {
        const responseData = error.response.data || {};
        const statusCode = error.response.status;

        if (statusCode === 430 && responseData.code === 'SESSION_EXPIRED') {
          if (window.location.hash !== '#/public/session-conflict') {
            clearAuthData();
            removeAccessToken();
            removeUserId();
            Cookies.remove('refresh_token');
            window.location.href = '/#/public/session-conflict';
          }
          return Promise.reject(error);
        }

        if (responseData.errorCode === 430) {
          if (window.location.hash !== '#/public/login') {
            handleRevokedSession();
          }
          return Promise.reject(error);
        }

        if (statusCode === 430 && responseData?.code === 'SESSION_EXISTS') {
          return Promise.reject(error);
        }

        console.error('Server responded with an error:', error.response.data);

        // Missing resource (404): still report so we see systemic data gaps
        // (many users → one Sentry issue). GET 404 stays silent in the UI.
        if (statusCode === 404) {
          reportMissingData({
            url: error.config?.url,
            method: error.config?.method,
            status: statusCode,
            responseMessage: responseData?.message,
          });
          if (isGetRequest(error)) {
            return Promise.reject(error);
          }
        }

        if (statusCode === 401) {
          if (!shouldSuppressCamSessionInterrupt()) {
            clearAuthData();
            window.location.href = '/#/public/login';
          }
          return Promise.reject(error);
        }

        if (!statusCode || statusCode >= 500) {
          reportUnexpected(error, {
            url: error.config?.url,
            method: error.config?.method,
            status: statusCode,
            responseMessage: responseData?.message,
          });
          toast.error(friendlyByStatus(statusCode), { style: { padding: 10 } });
        } else {
          toast(userFacingClientMessage(responseData?.message, statusCode), {
            style: { padding: 10 },
          });
        }
      } else if (error.request) {
        console.error('No response received from server:', error.request);
        toast(friendlyByStatus(undefined));
      } else {
        console.error('Error setting up request:', error.message);
        reportUnexpected(error);
      }
    } catch (ex) {
      console.log('Error: ', ex);
      reportUnexpected(ex);
    }
    return Promise.reject(error);
  }
);

export default http;
